import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { DropdownField, Text, useControlledField } from '@weave/design-system';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { useSelector } from 'react-redux';

import { injectParams, ONBOARDING } from '../../../../../../constants/paths';
import {
  selectCurrentLocationId,
  selectCurrentLocationParentId,
  selectCurrentLocationSlug,
} from '../../../../../../redux/actions/location';
import { HandoffSnapshot } from '../../../handoff-hub.types';
import { useSnapshotState } from './use-edit-handoff-state';
import {
  HandoffTypes,
  handoffTypesDisplayValuesMap,
} from './handoff-metrics/handoff.constants';
import { getHandoffFlow } from './handoff-flows';
import {
  useCreateOrUpdateHandoffHubSnapshotMutation,
  useGetChildrenLocationsQuery,
} from 'components/onboarding/handoff-hub/handoff-hub.queries';
import { useAlert } from '@weave/alert-system';
import {
  onboardingHistoryApi,
  onboardingProgressApi,
} from '../../.././../onboarding.api';
import {
  OnboardingEvent,
  OnboardingProgressStatus,
} from '@weave/schema-gen-ts/dist/shared/insys/activation/onboarding.pb';

interface Props {
  handoffSnapshot?: HandoffSnapshot;
}

export const AddEditHandoff = (props: Props) => {
  const alert = useAlert();
  const locationId = useSelector(selectCurrentLocationId);
  const locationSlug = useSelector(selectCurrentLocationSlug);
  const parentLocationId = useSelector(selectCurrentLocationParentId);

  const { data: siblingLocations } = useGetChildrenLocationsQuery(parentLocationId);

  const hasSiblingLocations = !!parentLocationId && (siblingLocations?.length ?? 0) > 1;

  const availableHandoffTypes = useMemo(
    () =>
      Object.values(HandoffTypes).filter(
        (type) => !getHandoffFlow(type).requiresSiblings || hasSiblingLocations
      ),
    [hasSiblingLocations]
  );

  const rootHandoffHubUrl = injectParams(ONBOARDING.handoff, { id: locationId });
  const history = useHistory();
  const navigateToRoot = () => history.push(rootHandoffHubUrl);

  const { mutate: createOrUpdateSnapshotMutation, isLoading: isUpdating } =
    useCreateOrUpdateHandoffHubSnapshotMutation();

  const { snapshot, setSnapshot } = useSnapshotState(props.handoffSnapshot);

  const handoffTypeFieldProps = useControlledField({
    type: 'dropdown',
    required: true,
    onChange: (handoffType: HandoffTypes) =>
      setSnapshot((state) => ({ ...state, handoffType })),
    value: snapshot.handoffType,
  });

  const saveSnapshot = (
    updates: Partial<HandoffSnapshot>,
    onSuccess: (snapshot: Partial<HandoffSnapshot>) => Promise<void> | void,
    isSubmit = false
  ): void => {
    const newSnapshot = { ...snapshot, ...updates };
    createOrUpdateSnapshotMutation(
      { snapshot: newSnapshot, isSubmit },
      {
        onSuccess: () => {
          setSnapshot(newSnapshot);
          onSuccess(newSnapshot);
        },
      }
    );
  };

  const handleSubmitHandoffSuccess = async (snapshot: Partial<HandoffSnapshot>) => {
    if (snapshot.handoffType !== HandoffTypes.OnboardingHandoff) {
      navigateToRoot();
      return;
    }

    try {
      const res =
        await onboardingProgressApi.getLatestOnboardingProgressStatusByLocationId(
          locationId
        );
      const isSelfActivationLocation =
        !!res.onboardingProgressStatus &&
        res.onboardingProgressStatus !==
          OnboardingProgressStatus.ONBOARDING_PROGRESS_STATUS_UNSPECIFIED;

      if (isSelfActivationLocation) {
        await onboardingHistoryApi.publishOnboardingEvent({
          slug: locationSlug,
          locationId,
          onboardingEvent: OnboardingEvent.ONBOARDING_EVENT_HANDOFF_COMPLETED,
        });
      }
    } catch (error) {
      alert.error('Error getting latest onboarding progress status');
      console.error('Error getting latest onboarding progress status', error);
    }

    navigateToRoot();
  };

  const typeSelector = !snapshot.id && (
    <div css={dropdownFieldContainer}>
      <div css={dropdownFieldLabelContainer}>
        <Text as="div" weight="bold">
          Select Handoff Type
        </Text>
        <Text as="div" size="small">
          Choose handoff type to begin
        </Text>
      </div>
      <DropdownField
        {...handoffTypeFieldProps}
        name="handoffType"
        label="Handoff Types"
        css={{ width: 'fit-content', minWidth: 320 }}
      >
        {availableHandoffTypes.map((type) => (
          <DropdownField.Option value={type} key={type}>
            {handoffTypesDisplayValuesMap[type]}
          </DropdownField.Option>
        ))}
      </DropdownField>
    </div>
  );

  const { EditFlow } = getHandoffFlow(snapshot.handoffType as HandoffTypes);

  return (
    <EditFlow
      key={snapshot.handoffType}
      snapshot={snapshot}
      isUpdating={isUpdating}
      saveSnapshot={saveSnapshot}
      onCancel={history.goBack}
      navigateToRoot={navigateToRoot}
      onSubmitSuccess={handleSubmitHandoffSuccess}
      typeSelector={typeSelector}
      siblingLocations={siblingLocations}
    />
  );
};

const dropdownFieldContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1.5)};
`;

const dropdownFieldLabelContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
`;
